export const CLIENT_TAG_PRESET_COLORS = [
  '#E53935',
  '#FB8C00',
  '#FDD835',
  '#43A047',
  '#00897B',
  '#1976D2',
  '#3949AB',
  '#8E24AA',
  '#D81B60',
  '#757575',
];

export const DEFAULT_CLIENT_TAG_COLOR = CLIENT_TAG_PRESET_COLORS[5];

export const CLIENT_TAG_RULE_FIELDS = [
  'unassignedItemsCount',
  'openChecksCount',
  'disabled',
  'country',
  'language',
  'reservationLimit',
];

export const CLIENT_TAG_RULE_OPERATORS = {
  eq: 'eq',
  ne: 'ne',
  gt: 'gt',
  gte: 'gte',
  lt: 'lt',
  lte: 'lte',
  empty: 'empty',
  notEmpty: 'notEmpty',
};

export function operatorsForRuleField(field) {
  if (field === 'unassignedItemsCount' || field === 'openChecksCount') {
    return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'];
  }
  if (field === 'disabled') {
    return ['eq'];
  }
  if (field === 'reservationLimit' || field === 'country' || field === 'language') {
    return ['eq', 'ne', 'empty', 'notEmpty'];
  }
  return Object.values(CLIENT_TAG_RULE_OPERATORS);
}

export function isValidHexColor(color) {
  return typeof color === 'string' && /^#[0-9A-Fa-f]{6}$/.test(color.trim());
}

export function normalizeHexColor(color, fallback = DEFAULT_CLIENT_TAG_COLOR) {
  const trimmed = String(color || '').trim();
  return isValidHexColor(trimmed) ? trimmed.toUpperCase() : fallback;
}

export function getContrastText(hexColor) {
  const hex = normalizeHexColor(hexColor).replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#1a1a1a' : '#ffffff';
}

export function createEmptyClientTag() {
  return {
    _id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: '',
    color: DEFAULT_CLIENT_TAG_COLOR,
  };
}

export function createEmptyClientTagRule(tagId = '') {
  return {
    _id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    tagId,
    enabled: true,
    conditions: [{ field: 'openChecksCount', operator: 'gt', value: 0 }],
  };
}

export function mapCompanyClientTags(data) {
  return Array.isArray(data?.clientTags)
    ? data.clientTags.map((tag) => ({
      _id: String(tag._id),
      name: tag.name || '',
      color: normalizeHexColor(tag.color),
    }))
    : [];
}

export function mapCompanyClientTagRules(data) {
  return Array.isArray(data?.clientTagRules)
    ? data.clientTagRules.map((rule) => ({
      _id: String(rule._id),
      tagId: String(rule.tagId),
      enabled: rule.enabled !== false,
      conditions: (rule.conditions || []).map((c) => ({
        field: c.field,
        operator: c.operator,
        value: c.value,
      })),
    }))
    : [];
}

export function buildClientTagsSavePayload(tags) {
  return {
    clientTags: (tags || []).map((tag) => ({
      ...(tag._id && !String(tag._id).startsWith('new-') ? { _id: tag._id } : {}),
      name: String(tag.name || '').trim(),
      color: normalizeHexColor(tag.color),
    })),
  };
}

export function buildClientTagRulesSavePayload(rules) {
  return {
    clientTagRules: (rules || []).map((rule) => ({
      ...(rule._id && !String(rule._id).startsWith('new-') ? { _id: rule._id } : {}),
      tagId: rule.tagId,
      enabled: rule.enabled !== false,
      conditions: (rule.conditions || []).map((c) => ({
        field: c.field,
        operator: c.operator,
        ...(c.operator === 'empty' || c.operator === 'notEmpty' ? {} : { value: c.value }),
      })),
    })),
  };
}

export function validateClientTagsForm(tags, t) {
  const names = new Set();
  for (const tag of tags || []) {
    const name = String(tag.name || '').trim();
    if (!name) {
      return t('settings.clientTags.nameRequired');
    }
    const lower = name.toLowerCase();
    if (names.has(lower)) {
      return t('settings.clientTags.duplicateName');
    }
    names.add(lower);
    if (!isValidHexColor(tag.color)) {
      return t('settings.clientTags.invalidColor');
    }
  }
  return '';
}

export function conditionNeedsValue(operator) {
  return operator !== 'empty' && operator !== 'notEmpty';
}

export function splitClientTagsBySource(tags) {
  const manual = [];
  const rule = [];
  for (const tag of tags || []) {
    if (tag.source === 'rule') rule.push(tag);
    else manual.push(tag);
  }
  return { manual, rule };
}

export function getManualClientTagIds(client) {
  if (Array.isArray(client?.clientTagIds) && client.clientTagIds.length > 0) {
    return client.clientTagIds.map(String);
  }
  return (client?.clientTags || [])
    .filter((tag) => tag.source === 'manual')
    .map((tag) => String(tag._id));
}

/** True when client has at least one of the selected tag ids (manual or rule). */
export function clientMatchesTagFilter(clientTags, filterTagIds) {
  const filters = (filterTagIds || []).map(String).filter(Boolean);
  if (filters.length === 0) return true;
  const clientIds = new Set((clientTags || []).map((tag) => String(tag._id)));
  return filters.some((id) => clientIds.has(id));
}
